import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FlaskConical, Barcode, Award, PackageOpen, ClipboardCheck, Microscope, Headphones, FileCheck2, ChevronDown, Check, BadgeCheck } from "lucide-react";
import { useCatalog } from "../context/CatalogContext";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import GrandOpeningBanner from "../components/GrandOpeningBanner";
import SeasonalPromoHero from "../components/SeasonalPromoHero";

const HERO_BG = "https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ABOUT_IMG = "https://images.pexels.com/photos/9259964/pexels-photo-9259964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1100";
const VIAL_HERO = "https://images.pexels.com/photos/5921722/pexels-photo-5921722.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1300&w=1000";

const FEATURES = [
  { icon: FlaskConical,   title: "Research Use Only",      desc: "Supplied exclusively for laboratory and scientific research environments." },
  { icon: Barcode,        title: "Batch Traceability",     desc: "Every release ships with a unique batch identifier and supporting records." },
  { icon: Award,          title: "Research Grade Quality", desc: "Strict supplier vetting and consistent handling. No compromise on integrity." },
  { icon: PackageOpen,    title: "Secure Shipping",        desc: "Discreet, temperature-aware logistics across the contiguous United States." },
  { icon: ClipboardCheck, title: "Research Integrity",     desc: "Full records available on every order. Product verification on request." },
  { icon: Microscope,     title: "Laboratory Standards",   desc: "Lyophilized, sealed, and processed to laboratory standards." },
  { icon: Headphones,     title: "Professional Support",   desc: "Direct line to our research operations team." },
  { icon: BadgeCheck,     title: "Third-Party Verified",   desc: "Every batch tested by an independent accredited laboratory before it ships." },
];

const FAQ = [
  {
    q: "Are these products for human consumption?",
    a: "No. All products listed on LuviaRx are intended exclusively for laboratory and scientific research purposes. They are not for human consumption under any circumstance.",
  },
  {
    q: "What does “research use only” mean?",
    a: "“Research use only” indicates that a compound is supplied for in-vitro investigation in qualified laboratory environments. It is not a drug, supplement, food, or cosmetic, and is not intended for diagnostic or therapeutic use.",
  },
  {
    q: "Are these products FDA approved?",
    a: "No. Compounds offered on this site are not FDA approved for diagnostic or therapeutic use. They have not been evaluated for safety or efficacy in humans.",
  },
  {
    q: "Do you provide certificates of analysis?",
    a: "Yes. A Certificate of Analysis (COA), including identity, purity (HPLC), and mass-spectrometry verification, is available upon request for each compound batch.",
  },
  {
    q: "Who are these products intended for?",
    a: "Compounds are intended exclusively for qualified researchers, laboratory professionals, and institutions engaged in lawful scientific investigation.",
  },
  {
    q: "What are proper storage recommendations?",
    a: "Lyophilized compounds should be stored sealed at refrigerated temperatures (2–8°C). For long-term storage, a controlled −20°C freezer is preferred. Always handle under appropriate laboratory conditions.",
  },
];

export default function Home() {
  const { products } = useCatalog();
  // Pick a curated featured set that showcases category breadth + a multi-variant product.
  const featuredSlugs = ["glp-3r", "bpc-157", "tesamorelin", "ghk-cu", "glow", "nad-plus"];
  const featured = featuredSlugs
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <main data-testid="home-page">
      <GrandOpeningBanner />
      <SEO
        title="LuviaRx | Premium Research Peptides & Compounds"
        description="US-based supplier of premium research peptides and compounds. Third-party tested, batch-traceable, and shipped from Boca Raton, FL. For Research Use Only."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "LuviaRx",
            legalName: "LuviaRx LLC",
            url: "https://luviarx.com",
            logo: "https://luviarx.com/brand/logo.png",
            image: "https://luviarx.com/og-image.png",
            description:
              "US-based supplier of premium research peptides and compounds. Third-party tested, batch-traceable, and shipped from Boca Raton, FL.",
            email: "support@luviarx.com",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Boca Raton",
              addressRegion: "FL",
              postalCode: "33486",
              addressCountry: "US",
            },
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "support@luviarx.com",
              areaServed: "US",
              availableLanguage: ["English"],
            },
            sameAs: [],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "LuviaRx",
            url: "https://luviarx.com",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://luviarx.com/products?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-cream-100 via-cream-100/95 to-cream-100" />

        <div className="container-x pt-16 md:pt-20 pb-[60px]">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-5 animate-fade-up">Modern Research Compounds</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[0.95] tracking-tight text-charcoal-600 animate-fade-up [animation-delay:80ms]">
                Elevated standards.<br />
                <span className="italic font-medium text-sage-600">Quietly precise.</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-charcoal-400 max-w-xl leading-relaxed animate-fade-up [animation-delay:160ms]">
                Premium peptide research compounds, supplied exclusively for laboratory and scientific research purposes. Verified, traceable, and handled to lab-grade standards.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 animate-fade-up [animation-delay:240ms]">
                <Link
                  to="/products"
                  data-testid="hero-cta-browse"
                  className="btn-primary"
                >
                  Browse Research Compounds
                  <ArrowUpRight className="w-4 h-4 ml-2" strokeWidth={2} />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-cta-learn"
                  className="btn-secondary"
                >
                  Learn More
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md animate-fade-up [animation-delay:320ms]">
                <Stat label="Purity threshold" value="≥ 99%" />
                <Stat label="Compound catalog" value="10+" />
                <Stat label="3rd-party tested" value="Every lot" />
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-up [animation-delay:200ms]">
              <div
                data-testid="hero-showcase-card"
                className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-soft"
                style={{ background: "#FDFAF9" }}
              >
                {/* Subtle grain texture */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.06] mix-blend-multiply pointer-events-none"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                  }}
                />

                {/* Soft plum accent blob (top-left, very subtle) */}
                <span
                  aria-hidden
                  className="absolute -top-8 -left-10 w-44 h-44 rounded-full blur-3xl"
                  style={{ background: "rgba(139, 34, 82, 0.10)" }}
                />
                {/* Plum accent dot (mid-left) — 30% opacity */}
                <span
                  aria-hidden
                  data-testid="hero-accent-sage"
                  className="absolute top-1/2 left-7 w-2 h-2 rounded-full"
                  style={{ background: "rgba(139, 34, 82, 0.30)", boxShadow: "0 0 18px 4px rgba(139, 34, 82, 0.18)" }}
                />

                {/* Plum accent dot — matches RX wordmark */}
                <span
                  aria-hidden
                  data-testid="hero-accent-orange"
                  className="absolute top-10 right-10 w-2.5 h-2.5 rounded-full"
                  style={{ background: "#8B2252", boxShadow: "0 0 22px 6px rgba(139, 34, 82, 0.40)" }}
                />

                {/* Headline */}
                <div className="absolute top-9 left-9 right-9">
                  <h2 className="font-display font-bold text-3xl md:text-[2.1rem] leading-[1.02] tracking-tight text-charcoal-700">
                    Precision.<br />
                    Purity.<br />
                    <span className="italic font-medium" style={{ color: "#8B2252" }}>Consistency.</span>
                  </h2>
                </div>

                {/* Vials cluster — three branded photos at varied heights / rotations */}
                <div className="absolute inset-x-0 bottom-24 md:bottom-28 flex items-end justify-center px-8">
                  <div className="relative w-full max-w-[320px] flex items-end justify-center">
                    {/* Sage / Recovery — BPC-157 (goes UP) */}
                    <figure
                      data-testid="hero-vial-recovery"
                      className="relative -mr-4 translate-y-4"
                    >
                      <img
                        src="/products/bpc-157.jpg"
                        alt="BPC-157 research compound"
                        className="w-24 h-32 md:w-[6.75rem] md:h-36 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.28)] ring-1 ring-white/40 animate-vial-up"
                        style={{ "--vial-rot": "-6deg" }}
                        loading="lazy"
                      />
                      <span
                        aria-hidden
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-16 h-2 rounded-full bg-charcoal-600/20 blur-md"
                      />
                    </figure>

                    {/* Fuchsia / NAD+ — center, tallest (goes DOWN) */}
                    <figure
                      data-testid="hero-vial-nad"
                      className="relative z-10 -translate-y-3"
                    >
                      <img
                        src="/products/nad-plus-100mg.jpg"
                        alt="NAD+ research compound"
                        className="w-28 h-36 md:w-32 md:h-40 object-cover rounded-2xl shadow-[0_30px_60px_-15px_rgba(60,40,55,0.34)] ring-1 ring-white/50 animate-vial-down"
                        style={{ "--vial-rot": "0deg" }}
                        loading="lazy"
                      />
                      <span
                        aria-hidden
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-2 rounded-full bg-charcoal-600/25 blur-md"
                      />
                    </figure>

                    {/* Burnt Orange / Metabolic — GLP-3R (goes UP — outer pair mirrors) */}
                    <figure
                      data-testid="hero-vial-metabolic"
                      className="relative -ml-4 translate-y-2"
                    >
                      <img
                        src="/products/glp-3r-30mg.jpg"
                        alt="GLP-3R research compound"
                        className="w-24 h-32 md:w-[6.75rem] md:h-36 object-cover rounded-2xl shadow-[0_22px_44px_-12px_rgba(60,40,55,0.28)] ring-1 ring-white/40 animate-vial-up"
                        style={{ "--vial-rot": "6deg" }}
                        loading="lazy"
                      />
                      <span
                        aria-hidden
                        className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-16 h-2 rounded-full bg-charcoal-600/20 blur-md"
                      />
                    </figure>
                  </div>
                </div>

                {/* Subtitle anchored at bottom */}
                <div className="absolute bottom-7 left-9 right-9">
                  <p className="text-[13px] md:text-sm leading-relaxed text-charcoal-500 max-w-[300px]">
                    Every batch is traceable, verified, and produced to rigorous research standards.
                  </p>
                </div>
              </div>

              {/* Outside decorative blobs (existing — preserved for depth) */}
              <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-cream-300 -z-10" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-sage-100 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED COMPOUNDS */}
      <SeasonalPromoHero />
      <section id="featured" className="py-12 md:py-16">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="overline mb-3">Featured Compounds</p>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600 max-w-2xl">
                A curated catalog of research-grade peptides.
              </h2>
            </div>
            <Link to="/products" data-testid="see-all-products" className="btn-ghost">
              See all compounds <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featured.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>

          <p className="mt-10 text-xs text-charcoal-300 max-w-3xl leading-relaxed">
            All compounds listed are supplied strictly for laboratory and scientific research purposes. Not for human consumption. Not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        className="py-16 md:py-24 overflow-hidden"
        style={{ backgroundColor: "#F5EAE6", minHeight: 500 }}
      >
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            {/* Oversized floating vials — staggered diagonal, no card chrome.
                Container is intentionally clipped on overflow so the right
                column is never crowded. */}
            <div
              data-testid="about-vials-canvas"
              className="relative w-full overflow-hidden"
              style={{ height: 600 }}
            >
              {/* Tesamorelin — top-left, anchored to the canvas */}
              <img
                src="/products/tesamorelin-floating.png"
                alt="Tesamorelin research vial"
                data-testid="about-vial-tesamorelin"
                className="absolute z-10"
                style={{
                  width: "55%", maxWidth: 260,
                  top: 0, left: 0,
                  transform: "rotate(-3deg)",
                  filter: "drop-shadow(0 28px 32px rgba(28,22,24,0.22))",
                }}
                loading="lazy"
              />

              {/* GLP-3R 10mg — top-right, slightly lower, overlaps Tesamorelin */}
              <img
                src="/products/glp-3r-floating.png"
                alt="GLP-3R research vial"
                data-testid="about-vial-glp3r"
                className="absolute z-20"
                style={{
                  width: "58%", maxWidth: 280,
                  top: 70, right: 0,
                  transform: "rotate(4deg)",
                  filter: "drop-shadow(0 32px 36px rgba(28,22,24,0.24))",
                }}
                loading="lazy"
              />

              {/* BPC-157 — bottom-center, sits in front, overlaps the pair above */}
              <img
                src="/products/bpc-157-floating.png"
                alt="BPC-157 research vial"
                data-testid="about-vial-bpc157"
                className="absolute z-30"
                style={{
                  width: "62%", maxWidth: 300,
                  bottom: 0, left: "20%",
                  transform: "rotate(-2deg)",
                  filter: "drop-shadow(0 36px 44px rgba(28,22,24,0.26))",
                }}
                loading="lazy"
              />

              {/* Floating badges — unified style: white pill, burgundy border,
                  burgundy text. Each sits next to its specific vial. */}
              <span
                data-testid="about-badge-purity"
                className="absolute z-40 inline-flex items-center font-medium text-xs px-3.5 py-1.5 rounded-full bg-white"
                style={{
                  top: 36, left: 0,
                  color: "#8B2252",
                  border: "1px solid rgba(139,34,82,0.35)",
                  boxShadow: "0 10px 24px -10px rgba(28,22,24,0.22)",
                  letterSpacing: "0.04em",
                }}
              >
                ≥ 99% Purity
              </span>
              <span
                data-testid="about-badge-research-grade"
                className="absolute z-40 inline-flex items-center font-medium text-xs px-3.5 py-1.5 rounded-full bg-white"
                style={{
                  top: 110, right: 0,
                  color: "#8B2252",
                  border: "1px solid rgba(139,34,82,0.35)",
                  boxShadow: "0 10px 24px -10px rgba(28,22,24,0.22)",
                  letterSpacing: "0.04em",
                }}
              >
                Research Grade
              </span>
              <span
                data-testid="about-badge-batch-tested"
                className="absolute z-40 inline-flex items-center font-medium text-xs px-3.5 py-1.5 rounded-full bg-white"
                style={{
                  bottom: 32, left: 4,
                  color: "#8B2252",
                  border: "1px solid rgba(139,34,82,0.35)",
                  boxShadow: "0 10px 24px -10px rgba(28,22,24,0.22)",
                  letterSpacing: "0.04em",
                }}
              >
                Batch Tested
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="overline mb-3">About LuviaRx</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600 mb-6">
              The standard your research deserves.
            </h2>
            <p className="text-lg text-charcoal-400 leading-relaxed mb-5">
              LuviaRx provides premium research compounds intended exclusively for laboratory and scientific research environments. Every compound we carry is selected and handled with one principle in mind: <span className="text-charcoal-600 font-medium">research integrity</span>.
            </p>
            <ul className="space-y-3 text-charcoal-500">
              {[
                "Every release ships with a unique batch identifier",
                "Batch records maintained and referenceable for every order",
                "Supplied exclusively for laboratory and scientific research use",
                "Lyophilized, sealed, and processed to laboratory standards",
                "Full records available on every order. Product verification on request.",
              ].map((line) => (
                <li key={line} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "#8B2252" }} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-12 md:py-16">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline mb-3">Why LuviaRx</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600">
              Research-grade by every measure.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="surface-card p-7 md:p-8 hover:shadow-hover transition-all duration-500 hover:-translate-y-1 animate-fade-up"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(139, 34, 82, 0.08)" }}>
                  <f.icon className="w-5 h-5" strokeWidth={1.7} style={{ color: "#8B2252" }} />
                </div>
                <h3 className="font-display text-[20px] font-bold text-charcoal-600 mb-2">{f.title}</h3>
                <p className="text-sm text-charcoal-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12 md:py-16 bg-cream-200/50">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline mb-3">FAQ</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight text-charcoal-600 mb-4">
              Common research questions, answered clearly.
            </h2>
            <p className="text-charcoal-400 leading-relaxed">
              Questions about compliance, sourcing, or storage? We have answers. Reach our team directly at support@luviarx.com.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="space-y-3">
              {FAQ.map((item, i) => (
                <FaqItem key={i} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE STRIP */}
      <section className="pt-[40px] pb-16 md:pb-20 bg-charcoal-600 text-cream-100">
        <div className="container-x text-center">
          <p className="overline text-sage-300 mb-4">Important Notice</p>
          <h3
            className="font-display text-2xl md:text-3xl font-bold leading-snug max-w-3xl mx-auto !text-cream-100"
            style={{ color: "#F4EDE2" }}
          >
            All products listed are for research use only. Not for human consumption. Not intended to diagnose, treat, cure, or prevent any disease.
          </h3>
          <p className="mt-4 text-sm text-cream-200/80">Not FDA approved for diagnostic or therapeutic use.</p>
        </div>
      </section>
    </main>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="font-display text-2xl md:text-3xl font-bold text-charcoal-600">{value}</p>
    <p className="text-[11px] uppercase tracking-[0.18em] text-charcoal-300 mt-1 font-medium leading-tight">{label}</p>
  </div>
);

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = React.useState(index === 0);
  return (
    <div
      data-testid={`faq-item-${index}`}
      className={`surface-card transition-all duration-300 ${open ? "shadow-hover" : ""}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6"
      >
        <span className="font-display font-bold text-charcoal-600 text-base md:text-lg">{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-charcoal-400 transition-transform duration-300 shrink-0 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.8}
        />
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-charcoal-400 leading-relaxed text-sm md:text-base animate-fade-in">
          {item.a}
        </div>
      )}
    </div>
  );
};
